import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { ChevronLeft } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useConfirmDialogs } from "@/hooks/use-confirm-dialog";
import { CloseButton } from "./ModalHeader";
import { ScrollFadeArea } from "./ScrollFadeArea";
import { StepIndicator } from "./StepIndicator";

export interface WizardStep {
  key: string;
  /** 스텝 표시 옆에 노출되는 제목 */
  title: string;
  /** 제목 아래 설명. 고객사명 강조 등을 담을 수 있게 ReactNode */
  description?: React.ReactNode;
  /** 하단 버튼 라벨 */
  nextLabel: string;
  /** false 면 하단 버튼 비활성 */
  canProceed: boolean;
  /** 진행불가 케이스 확인. false 를 반환하면 이동하지 않는다 */
  onNext: () => boolean | Promise<boolean>;
  content: React.ReactNode;
  /** 하단 버튼 위에 고정되는 요소 (예: 선택 요약 + 칩 목록). 스크롤되지 않는다 */
  footerExtra?: React.ReactNode;
}

interface WizardModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  /** 런타임에 길이가 바뀔 수 있다 (다른 위저드로 분기하는 경우) */
  steps: WizardStep[];
  /** 0-based */
  currentStep: number;
  onStepChange: (i: number) => void;
  /** 마지막 스텝의 onNext 가 true 를 반환했을 때 호출 */
  onComplete: () => void | Promise<void>;
  /** 닫기 시 이탈 확인 다이얼로그 */
  confirmOnClose?: boolean;
  containerClassName?: string;
  /** 본문 스크롤 영역 className. 하단 패딩(pb-9)을 덮는 용도다 */
  bodyClassName?: string;
}

/**
 * 다단계 등록 모달. `FormModal` 은 단일 스텝 전제라 확장하지 않고 별도로 둔다.
 *
 * **폼 상태를 소유하지 않는다.** 각 스텝이 자기 `useForm` 을 갖고, 위저드는 스텝 이동만
 * 담당한다. 비활성 스텝의 `content` 는 언마운트되므로 단계 간 유지가 필요한 값은
 * `onNext` 에서 호출부 state 로 올려야 한다.
 *
 * @example
 * ```tsx
 * const [step, setStep] = React.useState(0);
 * const [business, setBusiness] = React.useState<BusinessSummary | null>(null);
 *
 * <WizardModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="고객사 등록"
 *   currentStep={step}
 *   onStepChange={setStep}
 *   onComplete={handleSubmit}
 *   confirmOnClose
 *   steps={[
 *     {
 *       key: "search",
 *       title: "고객사 검색",
 *       nextLabel: "이 고객사 추가하기",
 *       canProceed: selected !== null,
 *       onNext: () => {
 *         setBusiness(selected);
 *         return true;
 *       },
 *       content: <ClientSearchStep ... />,
 *     },
 *   ]}
 * />
 * ```
 */
export function WizardModal({
  open,
  onOpenChange,
  title,
  steps,
  currentStep,
  onStepChange,
  onComplete,
  confirmOnClose = false,
  containerClassName,
  bodyClassName,
}: WizardModalProps) {
  // 버튼 disabled 는 눈에 보이는 피드백일 뿐이다 — 실제 중복 진행은 이 플래그가 막는다
  const [isAdvancing, setIsAdvancing] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const stepCount = steps.length;
  // 스텝 배열이 런타임에 교체되면 호출부의 currentStep 이 잠시 범위를 벗어날 수 있다
  const activeIndex =
    stepCount === 0 ? -1 : Math.min(Math.max(currentStep, 0), stepCount - 1);
  const activeStep = activeIndex === -1 ? null : steps[activeIndex];
  const isFirstStep = activeIndex <= 0;
  const isLastStep = activeIndex === stepCount - 1;

  const dialogs = useConfirmDialogs({
    exit: {
      title: `편집 중인 내용이 있어요.\n저장하지 않고 나갈까요?`,
      description: "저장하지 않은 내용은 삭제돼요.",
      confirmText: "나가기",
      cancelText: "취소",
      variant: "danger",
    },
  });

  // 스텝이 바뀌면 본문 스크롤을 맨 위로 되돌린다
  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeIndex]);

  const handleClose = () => {
    setIsAdvancing(false);
    onOpenChange(false);
  };

  const handleCloseAttempt = () => {
    if (confirmOnClose) {
      dialogs.open("exit", handleClose);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    if (isFirstStep) return;
    onStepChange(activeIndex - 1);
  };

  const handleNext = async () => {
    if (!activeStep || isAdvancing) return;

    setIsAdvancing(true);
    try {
      const canAdvance = await activeStep.onNext();
      if (!canAdvance) return;

      if (isLastStep) {
        await onComplete();
      } else {
        onStepChange(activeIndex + 1);
      }
    } finally {
      setIsAdvancing(false);
    }
  };

  return (
    <>
      <DialogPrimitive.Root
        open={open}
        onOpenChange={(val) => {
          if (val) {
            onOpenChange(true);
            // 진행 도중 닫았다 다시 열어도 하단 버튼이 비활성으로 남지 않게 한다
            setIsAdvancing(false);
          } else handleCloseAttempt();
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-50 bg-bg-dim",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            )}
          />
          <DialogPrimitive.Content
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              handleCloseAttempt();
            }}
            onPointerDownOutside={(e) => {
              e.preventDefault();
              handleCloseAttempt();
            }}
            onFocusOutside={(e) => {
              e.preventDefault();
            }}
            className={cn(
              "fixed left-1/2 top-1/2 z-50",
              "-translate-x-1/2 -translate-y-1/2",
              "w-200 max-h-[85vh]",
              "bg-bg-primary rounded-2xl overflow-hidden",
              "inset-border-solid-normal",
              "flex flex-col",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "outline-none",
              containerClassName,
            )}
          >
            <DialogPrimitive.Description className="sr-only">
              {title} 단계별 입력
            </DialogPrimitive.Description>

            {/* 타이틀 줄을 28px 로 고정한다 — [이전] 버튼(36px)이 헤더 높이를 늘리면
                버튼이 없는 첫 스텝과 헤더 높이가 달라진다 */}
            <div className="flex px-10 pt-10 pb-5 shrink-0 inset-border-solid-normal-b">
              <div className="flex h-7 flex-1 items-center gap-3">
                {!isFirstStep && (
                  <Button
                    type="button"
                    theme="assistive"
                    variant="outline"
                    size="sm"
                    onClick={handleBack}
                  >
                    <ChevronLeft />
                    이전
                  </Button>
                )}
                <DialogPrimitive.Title className="flex-1 typo-heading-1-bold text-labels-primary">
                  {title}
                </DialogPrimitive.Title>
                <CloseButton onClose={handleCloseAttempt} />
              </div>
            </div>

            {activeStep && (
              <>
                {/* Contents - 스텝 표시부터 함께 스크롤된다 */}
                <ScrollFadeArea
                  wrapperClassName="flex-1 min-h-0 flex flex-col"
                  scrollRef={scrollRef}
                  className={cn(
                    "flex flex-1 min-h-0 flex-col gap-6 px-10 pt-6 pb-9",
                    bodyClassName,
                  )}
                >
                  <div className="flex shrink-0 flex-col gap-3">
                    <StepIndicator steps={steps} currentStep={activeIndex} />
                    {activeStep.description && (
                      <div className="typo-body-2-medium text-labels-alternative">
                        {activeStep.description}
                      </div>
                    )}
                  </div>

                  {activeStep.content}
                </ScrollFadeArea>

                {/* Footer */}
                <div className="flex flex-col px-10 pt-6 pb-10 shrink-0">
                  {activeStep.footerExtra}
                  <Button
                    type="button"
                    theme="gradient"
                    variant="default"
                    size="lg"
                    className="w-full"
                    disabled={!activeStep.canProceed || isAdvancing}
                    onClick={handleNext}
                  >
                    {activeStep.nextLabel}
                  </Button>
                </div>
              </>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <dialogs.ConfirmDialogs />
    </>
  );
}

WizardModal.displayName = "WizardModal";
